import {
  ButtonLink,
  CarrierLogo,
  Column,
  FormCheckBox,
  Loader,
  ModalBase,
  Row,
  Typography,
} from '@/components'
import { WithInfoText } from '@/components/hoc/WithInfoText'
import { ComponentColors, ThemeColor } from '@/enums'
import { FormProvider, useFormContext } from '@/providers'
import {
  useDownloadPublicFile,
  usePublicCheckoutOverview,
  useUpdatePublicCheckoutOverview,
} from '@/services'
import { useLocationTypes } from '@/services/locationTypes'
import { theme } from '@/theme'
import {
  compactMoney,
  ddMMyyyy,
  pluralize,
  useLocalStorage,
  useModal,
} from '@/utils'
import { useCategoryTranslations } from '@/utils/hooks/useCategoryTranslations'
import { Button, Skeleton } from '@surein/ui'
import {
  Ambulance,
  Megaphone,
  MessageSquareHeart,
  PaintBucket,
  PlayCircle,
  ShieldCheck,
  Stethoscope,
  Trophy,
} from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { makeOverviewValidationSchema } from './validationSchema'

interface CarrierData {
  logoUrl: string
  name: string
}

export interface RecommendationProductFile {
  fileId: string
  name: string
}

interface CheckoutOverviewData {
  carrierData: CarrierData
  offerTag: string
  amountInsuredUnlimited: boolean
  amountInsured: number
  deductible: number
  startDate: string
  paymentPeriod: string
  locationType: string
  locationSubtypes: string[]
  recommendationProductFiles: RecommendationProductFile[]
  recommendationId: string
  recommendationProductId: string
}

interface ProtectionInformationProps {
  categoryUsps: string[]
  categoryId: string
  onButtonClick?: () => void
  buttonText?: string
  iconColor?: string
}

interface InformationModalProps {
  isOpen: boolean
  onClose: () => void
  categoryId: string
  offerTag: string
  downloadAllFiles: () => void
  shouldShowDownloadAllFiles: boolean
}

interface ButtonState {
  onClick?: () => void
  loading?: boolean
}

interface OverviewStepFormProps {
  setButtonState: (state: ButtonState) => void
  checked: boolean
  data: CheckoutOverviewData
}

interface PublicCheckoutOverviewStepProps {
  onNext: () => void
  setButtonState: (state: ButtonState) => void
}

const categoryIcons: Record<
  string,
  Array<React.ComponentType<{ color?: string }>>
> = {
  LI: [Ambulance, Stethoscope, PaintBucket, Megaphone],
}

const StyledCarrierLogo = styled(CarrierLogo)`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.glass200};
`

const RecommendedBadge = styled.div`
  background-color: ${({ theme }) => theme.color.glass100};
  padding: 4px 8px;
  border-radius: 100px;
  flex-shrink: 0;
  width: fit-content;
`

const DetailsButton = styled(ButtonLink)`
  text-decoration: underline;
  text-underline-offset: 2px;
  color: ${({ theme }) => theme.color.info1};

  &:hover {
    color: ${({ theme }) => theme.color.info1};
  }
`

const ProtectionEntry = styled(Row)`
  gap: 8px;
  align-items: center;
  > svg {
    flex-shrink: 0;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  & > div:last-child {
    grid-column: span 2;
  }
`

const CheckboxContainer = styled(Row)`
  & > div > div {
    align-items: center;
  }
`

const InformationContainer = styled(Column)`
  gap: 16px;
  padding: 0 8px;

  > span:first-child {
    padding-right: 32px;
  }
`

const WhatIsProtectedCard = styled(Column)`
  background-color: ${({ theme }) => theme.color.glass100};
  padding: 16px;
  border-radius: 16px;
  gap: 16px;
`

const InfoDescription = styled(Typography).attrs({
  variant: 'description',
})`
  font-weight: 500;
`

const ProtectionInformation: React.FC<ProtectionInformationProps> = ({
  categoryUsps,
  categoryId,
  onButtonClick,
  buttonText,
  iconColor,
}) => {
  const { t } = useTranslation()

  return (
    <WhatIsProtectedCard>
      <Row justifyContent="space-between">
        <Typography variant="subheadline">
          {t('checkout.overview.whatIsProtected')}
        </Typography>
        {onButtonClick && buttonText && (
          <DetailsButton
            onClick={onButtonClick}
            size="small"
            color={ComponentColors.primary}
          >
            {buttonText}
          </DetailsButton>
        )}
      </Row>
      <Column gap="8px">
        {categoryUsps.map((usp, index) => {
          const Icon = categoryIcons[categoryId]?.[index] || ShieldCheck
          return usp ? (
            <ProtectionEntry key={usp}>
              <Icon color={iconColor} />
              <Typography variant="description">{usp}</Typography>
            </ProtectionEntry>
          ) : null
        })}
      </Column>
    </WhatIsProtectedCard>
  )
}

const InformationModal: React.FC<InformationModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  offerTag,
  downloadAllFiles,
  shouldShowDownloadAllFiles,
}) => {
  const { t } = useTranslation()
  const categoryTranslations = useCategoryTranslations(categoryId)
  const categoryUsps = categoryTranslations.usps

  return (
    <ModalBase closable isOpen={isOpen} onClose={onClose} padding="16px">
      <InformationContainer>
        <Typography bold variant="subheadline">
          {categoryTranslations.categoryName}
        </Typography>
        <RecommendedBadge>
          <Typography variant="footnote" color={ThemeColor.success1}>
            {t(`${offerTag}`)}
          </Typography>
        </RecommendedBadge>
        <Column gap="16px">
          <Row gap="8px">
            <MessageSquareHeart />
            <Typography variant="description">
              {t('checkout.overview.cancelAnytime')}
            </Typography>
          </Row>
          <Row gap="8px">
            <PlayCircle />
            <Typography variant="description">
              {t('checkout.overview.insuranceCertificatePdf')}
            </Typography>
          </Row>
          <Row gap="8px">
            <Trophy />
            <Typography variant="description">
              {t('checkout.overview.topPerformance')}
            </Typography>
          </Row>
        </Column>
        <ProtectionInformation
          categoryUsps={categoryUsps}
          categoryId={categoryId}
          iconColor={theme.color.info1}
        />
        <Typography variant="footnote" color="glass500">
          {t('checkout.overview.insuranceOverviewNote')}
        </Typography>
        {shouldShowDownloadAllFiles && (
          <Button variant="primary" shape="square" onClick={downloadAllFiles}>
            {t('checkout.overview.readAllConditions')}
          </Button>
        )}
      </InformationContainer>
    </ModalBase>
  )
}

const makeLocationSubtypes = (
  locationLabelGetter: (subtype: string) => string,
  locationSubtypes: string[],
): string =>
  locationSubtypes.map((subtype) => locationLabelGetter(subtype)).join(', ')

const useMessages = () => {
  const { t } = useTranslation()
  return {
    validations: t('validations', { returnObjects: true }),
    paymentPeriod: t('payment_period'),
    paymentPeriodOptions: t('paymentPeriodOptions', { returnObjects: true }),
    sepaDebit: t('sepa_debit'),
    change: t('change'),
    checkoutOverviewCheckbox: t('checkout_overview_checkbox'),
    startDateNotValid: t('start_date_not_valid'),
    startDateNotValidLe: t('start_date_not_valid_le'),
    startDate: t('starting_date'),
    startDateDescription: t('start_date_description'),
    topPerformance: t('checkout.topPerformance'),
    whatIsProtected: t('whatIsProtected'),
    amountInsured: t('amountInsured'),
    deductible: t('deductible'),
    coverage: t('coverage'),
    cancellation: t('cancellation'),
    cancelTwoWeeksAfterSigning: t('cancelTwoWeeksAfterSigning'),
    acceptInsuranceTerms: t('acceptInsuranceTerms'),
    unlimited: t('unlimited'),
    areDeemedToBeCoinsured: t('areDeemedToBeCoinsured'),
    isDeemedToBeCoinsured: t('isDeemedToBeCoinsured'),
  }
}

const OverviewStepForm: React.FC<OverviewStepFormProps> = ({
  setButtonState,
  checked,
  data,
}) => {
  const { t } = useTranslation()
  const { categoryId } = useParams<{ categoryId: string }>()
  const messages = useMessages()
  const { submitForm, customValidate } = useFormContext()
  const {
    data: locationTypesData,
    isLoading: locationTypesLoading,
  } = useLocationTypes()

  if (!categoryId) {
    throw new Error('Category ID is required')
  }

  const categoryTranslations = useCategoryTranslations(categoryId)
  const categoryUsps = categoryTranslations.usps

  const {
    open: openInformationModal,
    close: closeInformationModal,
    isOpen: isInformationModalOpen,
  } = useModal()

  const downloadFileMutation = useDownloadPublicFile()

  const downloadAllFiles = () => {
    data?.recommendationProductFiles.forEach((file) => {
      downloadFileMutation.mutate({
        fileId: file.fileId,
        confirmationId: data.recommendationId,
      })
    })
  }

  const handleSuccess = () => {
    submitForm()
  }

  useEffect(() => {
    setButtonState({
      onClick: () =>
        customValidate({
          onSuccess: handleSuccess,
        }),
    })
  }, [])

  return (
    <Column gap="16px">
      <Column gap="16px">
        <Row alignItems="center" justifyContent="space-between" gap="16px">
          <Row alignItems="center" gap="8px">
            <StyledCarrierLogo name={data.carrierData.logoUrl} />
            <Typography ellipsis variant="p2Body">
              {data.carrierData.name}
            </Typography>
          </Row>
          <RecommendedBadge>
            <Typography
              noWordBreak
              variant="footnote"
              color={ThemeColor.success1}
            >
              {t(`${data.offerTag}`)}
            </Typography>
          </RecommendedBadge>
        </Row>
        <ProtectionInformation
          categoryUsps={categoryUsps}
          categoryId={categoryId}
          onButtonClick={openInformationModal}
          buttonText={t('checkout.overview.details')}
        />
        <GridContainer>
          <Column gap="4px">
            <WithInfoText
              variant="secondary"
              content={t('checkout.overview.insuredSumInfo')}
            >
              <Typography variant="footnote" color="glass500">
                {t('checkout.overview.insuredSum')}
              </Typography>
            </WithInfoText>
            <InfoDescription>
              {data.amountInsuredUnlimited
                ? t('unlimited')
                : compactMoney(data.amountInsured)}
            </InfoDescription>
          </Column>
          <Column gap="4px">
            <WithInfoText
              variant="secondary"
              content={t('checkout.overview.deductibleInfo')}
            >
              <Typography variant="footnote" color="glass500">
                {t('checkout.overview.deductible')}
              </Typography>
            </WithInfoText>
            <InfoDescription>{compactMoney(data.deductible)}</InfoDescription>
          </Column>
          <Column gap="4px">
            <WithInfoText
              variant="secondary"
              content={t('checkout.overview.startDateInfo')}
            >
              <Typography variant="footnote" color="glass500">
                {t('checkout.overview.startDate')}
              </Typography>
            </WithInfoText>
            <InfoDescription>{ddMMyyyy(data.startDate)}</InfoDescription>
          </Column>
          <Column gap="4px">
            <WithInfoText
              variant="secondary"
              content={t('checkout.overview.paymentPeriodInfo')}
            >
              <Typography variant="footnote" color="glass500">
                {t('checkout.overview.paymentPeriod')}
              </Typography>
            </WithInfoText>
            <InfoDescription>
              {t(`paymentPeriodOptions.${data.paymentPeriod}`)}
            </InfoDescription>
          </Column>
          <Column gap="4px">
            <WithInfoText
              variant="secondary"
              content={t('checkout.overview.coverageInfo')}
            >
              <Typography variant="footnote" color="glass500">
                {t('checkout.overview.coverage')}
              </Typography>
            </WithInfoText>
            {locationTypesLoading ? (
              <Skeleton width="240px" />
            ) : (
              <InfoDescription>
                {locationTypesData?.getLocationLabel(data.locationType)}
                {data.locationSubtypes.length
                  ? ` - ${makeLocationSubtypes(
                      (subtype: string) =>
                        locationTypesData?.getLocationLabel(subtype) ?? '',
                      data.locationSubtypes,
                    )} ${pluralize(
                      data.locationSubtypes.length,
                      messages.isDeemedToBeCoinsured,
                      messages.areDeemedToBeCoinsured,
                      false,
                    )}`
                  : ''}
              </InfoDescription>
            )}
          </Column>
        </GridContainer>
      </Column>
      <WithInfoText
        variant="secondary"
        content={t('checkout.overview.insuranceTermsConfirmation')}
      >
        <CheckboxContainer>
          <FormCheckBox
            id="acceptTerms"
            label={t('checkout.overview.insuranceTermsConfirmation')}
            name="acceptTerms"
            noMargin
            initialValue={checked}
            ignoreErrorKeys={[]}
          />
        </CheckboxContainer>
      </WithInfoText>
      <InformationModal
        isOpen={isInformationModalOpen}
        onClose={closeInformationModal}
        categoryId={categoryId}
        offerTag={data.offerTag}
        shouldShowDownloadAllFiles={data.recommendationProductFiles.length > 0}
        downloadAllFiles={downloadAllFiles}
      />
    </Column>
  )
}

export const PublicCheckoutOverviewStep: React.FC<PublicCheckoutOverviewStepProps> = ({
  onNext,
  setButtonState,
}) => {
  const messages = useMessages()
  const { categoryId } = useParams<{ categoryId: string }>()
  const { data, isLoading } = usePublicCheckoutOverview()
  const [checked] = useLocalStorage<boolean>(
    `${categoryId}-public-checkout-overview-checkbox`,
    false,
  )

  if (!categoryId) {
    throw new Error('Category ID is required')
  }

  const updateCheckoutOverviewMutation = useUpdatePublicCheckoutOverview(
    (data as CheckoutOverviewData)?.recommendationProductId ?? '',
  )

  useEffect(() => {
    setButtonState({
      loading: updateCheckoutOverviewMutation.isLoading,
    })
  }, [updateCheckoutOverviewMutation.isLoading])

  const handleSubmit = (formValues: { startDate: string }) => {
    updateCheckoutOverviewMutation.mutate(formValues, { onSuccess: onNext })
  }

  if (isLoading || !data) return <Loader />

  const typedData = data as CheckoutOverviewData

  return (
    <FormProvider
      initialValues={{ startDate: typedData.startDate }}
      onSubmit={handleSubmit}
      validationSchema={makeOverviewValidationSchema(
        // @ts-expect-error left for later/never, if you're reading this, fix it
        messages.validations,
        categoryId,
      )}
    >
      <OverviewStepForm
        checked={checked}
        data={typedData}
        setButtonState={setButtonState}
      />
    </FormProvider>
  )
}
